import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { ensureIndexes } from '/imports/api/ensureIndex';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';

/**
 * Dynamic Card Lists: named, reusable card selection queries.
 *
 * A list encapsulates one advertising/trading strategy ("Standard", "Pauper",
 * "Commander Staples") as a single Mongo query against `cards` -- multiple
 * selection expressions compose inside it via $or. Fleets and Settings
 * REFERENCE lists by id rather than embedding queries, so editing a list
 * updates every bot that uses it; the referencing side never copies the query.
 *
 * The inline Fleet/Settings query fields this replaces are legacy: still in
 * their schemas for rollback, no longer read by anything.
 */
export interface CardList extends Doc {
  _id: string;
  /** Unique display name, e.g. 'Pauper'. */
  name: string;
  /** Mongo query against `cards`. Validated as executable at save time. */
  query: Record<string, unknown>;
  description?: string;
  /** Free-form labels; future categorisation hangs off these. */
  tags?: string[];
}

type Model = CardList;
type Refs = References<Model>;

export const CardLists = new Mongo.Collection<CardList>('cardLists');

Meteor.isServer && (async () => {
  // Unique: two strategies with one name is a config ambiguity, not a feature.
  await ensureIndexes(CardLists, [
    { keys: { name: 1 }, options: { unique: true } },
  ]);
})();

export const listCardLists = DeclareMethod<ListMethodArg<CardList>, ListMethodResponse<CardList, Refs>>('listCardLists');
export const getCardList = DeclareMethod<string, GetMethodResponse<CardList, Refs>>('getCardList');
export const createCardList = DeclareMethod<Partial<CardList>, string>('createCardList');
export const updateCardList = DeclareMethod<Partial<CardList>, string>('updateCardList');
export const deleteCardList = DeclareMethod<string, undefined>('deleteCardList');

/** Everything that references a list — shown on its page, and what makes a
 *  delete refusal explain itself. */
export const cardListReferences = DeclareMethod<{ listId: string }, {
  fleets: { _id: string; name: string; fields: string[] }[];
  settingsFields: string[];
}>('cardListReferences');

export const modelClass: ModelClass<Model, Refs> = {
  collection: CardLists,
  editableFields: ['name', 'query', 'description', 'tags'],
  searchQuery: (search?: string) => {
    search = search?.trim();
    return search?.length
      ? { $or: [{ name: new RegExp(search, 'i') }, { description: new RegExp(search, 'i') }] } as any
      : {};
  },
  listMethod: listCardLists,
  getMethod: getCardList,
  createMethod: createCardList,
  updateMethod: updateCardList,
  deleteMethod: deleteCardList,
  pathPrefix: '/card-lists',
  modelName: 'Card List',
  pluralName: 'Card Lists',
  icon: 'list',
};
